import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const emptyVisitForm = {
  appointment_id: '',
  visitor_name: '',
  visitor_phone: '',
  visitor_email: '',
  organisation: '',
  visitor_category: 'other',
  visit_nature: 'official',
  purpose: '',
  host_user_id: '',
  access_level: 'reception',
  identity_type: '',
  identity_last_four: '',
  visitor_badge_number: ''
};

const emptyAppointmentForm = {
  visitor_name: '',
  visitor_phone: '',
  visitor_email: '',
  organisation: '',
  visit_nature: 'official',
  purpose: '',
  host_user_id: '',
  scheduled_start: '',
  scheduled_end: '',
  location: 'Reception',
  notes: ''
};

const colours = {
  brown: '#3B2A21',
  green: '#556B2F',
  cream: '#F2EFE7',
  gold: '#A8893D',
  red: '#8F2E24',
  border: '#E4DED3',
  muted: '#756F68'
};

const statusLabel = value =>
  String(value || 'unknown')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

function StatusBadge({ value }) {
  const danger = [
    'denied',
    'declined',
    'rejected',
    'cancelled'
  ].includes(value);

  const success = [
    'admitted',
    'meeting',
    'checked_out',
    'acknowledged',
    'approved',
    'confirmed',
    'completed'
  ].includes(value);

  return (
    <span style={{
      display: 'inline-block',
      borderRadius: 20,
      padding: '4px 9px',
      fontSize: 10,
      fontWeight: 700,
      background: danger
        ? '#FBE9E7'
        : success
          ? '#E8F3E8'
          : '#FFF4D8',
      color: danger
        ? colours.red
        : success
          ? '#2E6B3A'
          : '#8B5E0A',
      textTransform: 'capitalize'
    }}>
      {statusLabel(value)}
    </span>
  );
}

function Field({
  label,
  required,
  children
}) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && (
          <span style={{ color: colours.red }}> *</span>
        )}
      </label>
      {children}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  colour
}) {
  return (
    <div className="card" style={{
      padding: 18,
      borderTop: `4px solid ${colour}`,
      minHeight: 100
    }}>
      <div style={{
        color: colours.muted,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.04em'
      }}>
        {label}
      </div>

      <div style={{
        color: colours.brown,
        fontSize: 30,
        fontWeight: 700,
        marginTop: 8
      }}>
        {value ?? 0}
      </div>
    </div>
  );
}

export default function FrontDesk() {
  const { user, hasPermission } = useAuth();

  const isManagingDirector =
    user?.role === 'super_admin';

  const canRead = hasPermission('front_desk:read');
  const canWrite = hasPermission('front_desk:write');

  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState({
    summary: {},
    waiting: [],
    appointments_today: []
  });
  const [visits, setVisits] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [visitForm, setVisitForm] =
    useState(emptyVisitForm);
  const [appointmentForm, setAppointmentForm] =
    useState(emptyAppointmentForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadData = useCallback(async () => {
    if (!canRead) {
      return;
    }

    setError('');

    try {
      const [
        dashboardResponse,
        visitResponse,
        appointmentResponse,
        hostResponse
      ] = await Promise.all([
        get('/front-desk/dashboard'),
        get('/front-desk/visits?limit=100'),
        get('/front-desk/appointments?limit=100'),
        get('/front-desk/hosts')
      ]);

      setDashboard(dashboardResponse || {
        summary: {},
        waiting: [],
        appointments_today: []
      });

      setVisits(visitResponse?.data || []);
      setAppointments(appointmentResponse?.data || []);
      setHosts(hostResponse?.data || []);
    } catch (err) {
      setError(
        err.message ||
        'Front Desk records could not be loaded'
      );
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    loadData();

    const timer = setInterval(loadData, 30000);

    return () => clearInterval(timer);
  }, [loadData]);

  const hostsForNature = nature =>
    nature === 'personal'
      ? hosts.filter(
          host => host.can_receive_personal
        )
      : hosts;

  const updateVisit = event => {
    const { name, value } = event.target;

    setVisitForm(current => {
      const next = {
        ...current,
        [name]: value
      };

      if (
        name === 'visit_nature' &&
        value === 'personal'
      ) {
        const selectedHost = hosts.find(
          host =>
            host.id === current.host_user_id
        );

        if (
          !selectedHost?.can_receive_personal
        ) {
          next.host_user_id = '';
        }
      }

      return next;
    });
  };

  const updateAppointment = event => {
    const { name, value } = event.target;

    setAppointmentForm(current => {
      const next = {
        ...current,
        [name]: value
      };

      if (
        name === 'visit_nature' &&
        value === 'personal'
      ) {
        const selectedHost = hosts.find(
          host =>
            host.id === current.host_user_id
        );

        if (
          !selectedHost?.can_receive_personal
        ) {
          next.host_user_id = '';
        }
      }

      return next;
    });
  };

  const submitVisit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    try {
      if (
        visitForm.identity_last_four &&
        !/^[A-Za-z0-9]{4}$/.test(
          visitForm.identity_last_four
        )
      ) {
        throw new Error(
          'Identity last four must contain exactly four letters or numbers'
        );
      }

      const payload = {
        ...visitForm,
        appointment_id:
          visitForm.appointment_id || null,
        host_user_id:
          visitForm.host_user_id || null,
        identity_type:
          visitForm.identity_type || null,
        identity_last_four:
          visitForm.identity_last_four || null,
        visitor_badge_number:
          visitForm.visitor_badge_number || null
      };

      const response = await post(
        '/front-desk/visits',
        payload
      );

      setNotice(
        `Visitor registered successfully. Reference: ${
          response?.data?.visit_ref || 'created'
        }`
      );

      setVisitForm(emptyVisitForm);
      setTab('visits');
      await loadData();
    } catch (err) {
      setError(
        err.message || 'Visitor registration failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const checkOutVisit = async record => {
    const departureNotes = window.prompt(
      `Optional departure notes for ${record.visitor_name}:`,
      ''
    );

    if (departureNotes === null) {
      return;
    }

    const confirmed = window.confirm(
      `Check out ${record.visitor_name}?`
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');

    try {
      await put(
        `/front-desk/visits/${record.id}/close`,
        {
          outcome: 'checked_out',
          departure_notes:
            departureNotes.trim() || null
        }
      );

      setNotice(
        `${record.visitor_name} has been checked out successfully.`
      );

      await loadData();
    } catch (err) {
      setError(
        err.message || 'Visitor checkout failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const closeWithoutMeeting = async record => {
    const suppliedReason = window.prompt(
      `Why did ${record.visitor_name} leave without meeting the host?`,
      ''
    );

    if (suppliedReason === null) {
      return;
    }

    const reason = suppliedReason.trim();

    if (!reason) {
      setError(
        'A reason is required before closing this visit.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Close ${record.visitor_name}'s visit as Left Without Meeting?`
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');

    try {
      await put(
        `/front-desk/visits/${record.id}/close`,
        {
          outcome: 'left_without_meeting',
          reason,
          departure_notes: reason
        }
      );

      setNotice(
        `${record.visitor_name}'s visit was closed as Left Without Meeting.`
      );

      await loadData();
    } catch (err) {
      setError(
        err.message ||
        'The visit could not be closed.'
      );
    } finally {
      setSaving(false);
    }
  };

  const submitAppointment = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    try {
      const payload = {
        ...appointmentForm,
        scheduled_start:
          new Date(
            appointmentForm.scheduled_start
          ).toISOString(),
        scheduled_end:
          appointmentForm.scheduled_end
            ? new Date(
                appointmentForm.scheduled_end
              ).toISOString()
            : null
      };

      const response = await post(
        '/front-desk/appointments',
        payload
      );

      setNotice(
        `${
          response?.message ||
          'Appointment submitted successfully'
        } Reference: ${
          response?.data?.appointment_ref || 'created'
        }`
      );

      setAppointmentForm(emptyAppointmentForm);
      setTab('appointments');
      await loadData();
    } catch (err) {
      setError(
        err.message || 'Appointment creation failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const decideAppointment = async (
    record,
    decision
  ) => {
    let reason = '';

    if (decision === 'rejected') {
      const suppliedReason = window.prompt(
        `Enter the reason for rejecting ${
          record.appointment_ref
        }:`
      );

      if (suppliedReason === null) {
        return;
      }

      reason = suppliedReason.trim();

      if (!reason) {
        setError('A rejection reason is required');
        return;
      }
    } else {
      const confirmed = window.confirm(
        `Approve appointment ${
          record.appointment_ref
        } for ${record.visitor_name}?`
      );

      if (!confirmed) {
        return;
      }

      reason = 'Approved by Managing Director';
    }

    setSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await put(
        `/front-desk/appointments/${
          record.id
        }/decision`,
        {
          decision,
          reason
        }
      );

      setNotice(
        response?.message ||
        `Appointment ${decision} successfully`
      );

      await loadData();
    } catch (err) {
      setError(
        err.message ||
        `Appointment could not be ${decision}`
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return (
      <div className="page">
        <div className="alert alert-error">
          You do not have permission to access Front Desk.
        </div>
      </div>
    );
  }

  const summary = dashboard.summary || {};
  const waiting = dashboard.waiting || [];
  const appointmentsToday =
    dashboard.appointments_today || [];

  const appointmentNeedsDecision = record =>
    record?.status === 'pending_approval' &&
    (
      !record.approval_status ||
      record.approval_status === 'pending'
    );

  const pendingAppointments = appointments.filter(
    appointmentNeedsDecision
  );

  const tabs = [
    ['dashboard', 'Live Dashboard'],
    ['visits', 'Visitor Records'],
    [
      'appointments',
      isManagingDirector && pendingAppointments.length > 0
        ? `Appointments (${pendingAppointments.length} pending)`
        : 'Appointments'
    ],
    ['register', 'Register Visitor'],
    ['schedule', 'Schedule Appointment']
  ];

  return (
    <div className="page">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 14,
        flexWrap: 'wrap',
        marginBottom: 18
      }}>
        <div>
          <h1 style={{
            margin: 0,
            color: colours.brown,
            fontSize: 26
          }}>
            Front Desk
          </h1>

          <p style={{
            margin: '5px 0 0',
            color: colours.muted,
            fontSize: 12
          }}>
            Appointments, visitor reception and access control
          </p>
        </div>

        <button
          className="btn btn-outline"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 18
      }}>
        {tabs.map(([key, label]) => {
          if (
            !canWrite &&
            ['register', 'schedule'].includes(key)
          ) {
            return null;
          }

          return (
            <button
              key={key}
              type="button"
              className={
                tab === key
                  ? 'btn btn-primary'
                  : 'btn btn-outline'
              }
              onClick={() => {
                setTab(key);
                setError('');
                setNotice('');
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="alert alert-error mb-md">
          {error}
        </div>
      )}

      {notice && (
        <div className="alert alert-success mb-md">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding: 25 }}>
          Loading Front Desk records...
        </div>
      ) : (
        <>
          {tab === 'dashboard' && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 12,
                marginBottom: 18
              }}>
                <SummaryCard
                  label="Arrivals Today"
                  value={summary.arrivals_today}
                  colour={colours.gold}
                />
                <SummaryCard
                  label="Waiting Now"
                  value={summary.waiting_now}
                  colour="#D48A16"
                />
                <SummaryCard
                  label="Inside Now"
                  value={summary.inside_now}
                  colour={colours.green}
                />
                <SummaryCard
                  label="Factory Approval Pending"
                  value={summary.factory_clearance_pending}
                  colour={colours.red}
                />
                <SummaryCard
                  label="Waiting Over 15 Minutes"
                  value={summary.waiting_over_15_minutes}
                  colour="#B53A32"
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 14
              }}>
                <div className="card">
                  <div className="card-header">
                    <strong>Visitors Waiting</strong>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Visitor</th>
                          <th>Host</th>
                          <th>Wait</th>
                          <th>Host Response</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waiting.length === 0 ? (
                          <tr>
                            <td colSpan="4">
                              No visitors are waiting.
                            </td>
                          </tr>
                        ) : waiting.map(record => (
                          <tr key={record.id}>
                            <td>
                              <strong>
                                {record.visitor_name}
                              </strong>
                              <div style={{
                                fontSize: 10,
                                color: colours.muted
                              }}>
                                {record.visit_ref}
                              </div>
                            </td>
                            <td>
                              {record.host_name || 'Not assigned'}
                            </td>
                            <td>
                              {record.waiting_minutes ?? 0} min
                            </td>
                            <td>
                              <StatusBadge
                                value={
                                  record.host_response_status
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <strong>Appointments Today</strong>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Visitor</th>
                          <th>Host</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointmentsToday.length === 0 ? (
                          <tr>
                            <td colSpan="4">
                              No appointments today.
                            </td>
                          </tr>
                        ) : appointmentsToday.map(record => (
                          <tr key={record.id}>
                            <td>
                              {new Date(
                                record.scheduled_start
                              ).toLocaleTimeString(
                                'en-NG',
                                {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }
                              )}
                            </td>
                            <td>{record.visitor_name}</td>
                            <td>
                              {record.host_name || 'Not assigned'}
                            </td>
                            <td>
                              <StatusBadge value={record.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {tab === 'visits' && (
            <div className="card">
              <div className="card-header">
                <strong>Visitor Records</strong>
                <span style={{
                  fontSize: 11,
                  color: colours.muted
                }}>
                  {visits.length} shown
                </span>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Visitor</th>
                      <th>Category</th>
                      <th>Host</th>
                      <th>Arrival</th>
                      <th>Host Response</th>
                      <th>Access</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.length === 0 ? (
                      <tr>
                        <td colSpan="8">
                          No visitor records yet.
                        </td>
                      </tr>
                    ) : visits.map(record => (
                      <tr key={record.id}>
                        <td>{record.visit_ref}</td>
                        <td>
                          <strong>{record.visitor_name}</strong>
                          <div style={{
                            fontSize: 10,
                            color: colours.muted
                          }}>
                            {record.organisation || ''}
                          </div>
                        </td>
                        <td>
                          {statusLabel(
                            record.visitor_category
                          )}
                        </td>
                        <td>
                          {record.host_name || 'Not assigned'}
                        </td>
                        <td>
                          <StatusBadge
                            value={record.arrival_status}
                          />
                        </td>
                        <td>
                          <StatusBadge
                            value={
                              record.host_response_status
                            }
                          />
                        </td>
                        <td>
                          {statusLabel(record.access_level)}
                        </td>
                        <td>
                          {canWrite &&
                           [
                             'checked_in',
                             'admitted',
                             'meeting'
                           ].includes(
                             record.arrival_status
                           ) ? (
                            <div style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap'
                            }}>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={saving}
                                onClick={() =>
                                  checkOutVisit(record)
                                }
                              >
                                Check Out
                              </button>

                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={saving}
                                onClick={() =>
                                  closeWithoutMeeting(record)
                                }
                              >
                                Left Without Meeting
                              </button>
                            </div>
                          ) : (
                            <span style={{
                              color: colours.muted,
                              fontSize: 11
                            }}>
                              Closed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'appointments' && (
          <div className="card">
            <div className="card-header">
              <strong>Appointments</strong>
              <span style={{
                fontSize: 11,
                color: colours.muted
              }}>
                {appointments.length} shown
                {isManagingDirector &&
                 pendingAppointments.length > 0
                  ? `, ${pendingAppointments.length} pending`
                  : ''}
              </span>
            </div>

            {isManagingDirector &&
             pendingAppointments.length > 0 && (
              <div style={{
                margin: '14px 18px 0',
                padding: '10px 12px',
                borderRadius: 6,
                background: '#FFF4D8',
                color: '#8B5E0A',
                fontSize: 12,
                fontWeight: 600
              }}>
                {pendingAppointments.length} appointment{
                  pendingAppointments.length === 1
                    ? ''
                    : 's'
                } awaiting your decision.
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Date and Time</th>
                    <th>Visitor</th>
                    <th>Host</th>
                    <th>Purpose</th>
                    <th>Appointment</th>
                    <th>Approval</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {appointments.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        No appointments yet.
                      </td>
                    </tr>
                  ) : appointments.map(record => {
                    const isPending =
                      appointmentNeedsDecision(record);

                    return (
                      <tr key={record.id}>
                        <td>
                          {record.appointment_ref}
                        </td>

                        <td>
                          {new Date(
                            record.scheduled_start
                          ).toLocaleString('en-NG')}
                        </td>

                        <td>{record.visitor_name}</td>

                        <td>
                          {record.host_name ||
                            'Not assigned'}
                        </td>

                        <td>{record.purpose}</td>

                        <td>
                          <StatusBadge
                            value={record.status}
                          />
                        </td>

                        <td>
                          <StatusBadge
                            value={
                              record.approval_status
                            }
                          />

                          {record.approval_reason && (
                            <div style={{
                              marginTop: 5,
                              maxWidth: 220,
                              color: colours.muted,
                              fontSize: 10,
                              lineHeight: 1.4
                            }}>
                              {record.approval_reason}
                            </div>
                          )}
                        </td>

                        <td>
                          {isManagingDirector &&
                           isPending ? (
                            <div style={{
                              display: 'flex',
                              gap: 6,
                              flexWrap: 'wrap'
                            }}>
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={saving}
                                onClick={() =>
                                  decideAppointment(
                                    record,
                                    'approved'
                                  )
                                }
                              >
                                Approve
                              </button>

                              <button
                                type="button"
                                className="btn btn-outline"
                                disabled={saving}
                                onClick={() =>
                                  decideAppointment(
                                    record,
                                    'rejected'
                                  )
                                }
                                style={{
                                  color: colours.red,
                                  borderColor: colours.red
                                }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span style={{
                              color: colours.muted,
                              fontSize: 11
                            }}>
                              No action required
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'register' && canWrite && (
            <form
              className="card"
              onSubmit={submitVisit}
            >
              <div className="card-header">
                <strong>Register Arriving Visitor</strong>
              </div>

              <div style={{
                padding: 18,
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(230px, 1fr))',
                gap: 14
              }}>
                <Field label="Existing Appointment">
                  <select
                    className="form-control"
                    name="appointment_id"
                    value={visitForm.appointment_id}
                    onChange={updateVisit}
                  >
                    <option value="">
                      Walk-in visitor
                    </option>
                    {appointments
                      .filter(record =>
                        ['scheduled', 'confirmed']
                          .includes(record.status)
                      )
                      .map(record => (
                        <option
                          key={record.id}
                          value={record.id}
                        >
                          {record.appointment_ref} - {
                            record.visitor_name
                          }
                        </option>
                      ))}
                  </select>
                </Field>

                <Field label="Visitor Name" required>
                  <input
                    className="form-control"
                    name="visitor_name"
                    value={visitForm.visitor_name}
                    onChange={updateVisit}
                    required
                  />
                </Field>

                <Field label="Phone Number">
                  <input
                    className="form-control"
                    name="visitor_phone"
                    value={visitForm.visitor_phone}
                    onChange={updateVisit}
                  />
                </Field>

                <Field label="Email Address">
                  <input
                    type="email"
                    className="form-control"
                    name="visitor_email"
                    value={visitForm.visitor_email}
                    onChange={updateVisit}
                  />
                </Field>

                <Field label="Organisation">
                  <input
                    className="form-control"
                    name="organisation"
                    value={visitForm.organisation}
                    onChange={updateVisit}
                  />
                </Field>

                <Field label="Visitor Category" required>
                  <select
                    className="form-control"
                    name="visitor_category"
                    value={visitForm.visitor_category}
                    onChange={updateVisit}
                  >
                    <option value="customer">Customer</option>
                    <option value="sales_prospect">
                      Sales Prospect
                    </option>
                    <option value="supplier">Supplier</option>
                    <option value="courier">Courier</option>
                    <option value="job_candidate">
                      Job Candidate
                    </option>
                    <option value="other">Other</option>
                  </select>
                </Field>

                <Field label="Visit Nature" required>
                  <select
                    className="form-control"
                    name="visit_nature"
                    value={visitForm.visit_nature}
                    onChange={updateVisit}
                    required
                  >
                    <option value="official">
                      Official Visit
                    </option>
                    <option value="personal">
                      Personal Visit
                    </option>
                  </select>
                </Field>

                <Field label={
                  visitForm.visit_nature === 'personal'
                    ? 'Host (Managing Director only)'
                    : 'Host'
                }>
                  <select
                    className="form-control"
                    name="host_user_id"
                    value={visitForm.host_user_id}
                    onChange={updateVisit}
                  >
                    <option value="">Select host</option>
                    {hostsForNature(
                      visitForm.visit_nature
                    ).map(host => (
                      <option
                        key={host.id}
                        value={host.id}
                      >
                        {host.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Access Requested" required>
                  <select
                    className="form-control"
                    name="access_level"
                    value={visitForm.access_level}
                    onChange={updateVisit}
                  >
                    <option value="reception">
                      Reception
                    </option>
                    <option value="office">Office</option>
                    <option value="factory">Factory</option>
                  </select>
                </Field>

                <Field label="Badge Number">
                  <input
                    className="form-control"
                    name="visitor_badge_number"
                    value={visitForm.visitor_badge_number}
                    onChange={updateVisit}
                  />
                </Field>

                <Field label="Identity Type">
                  <input
                    className="form-control"
                    name="identity_type"
                    placeholder="For example, NIN card"
                    value={visitForm.identity_type}
                    onChange={updateVisit}
                  />
                </Field>

                <Field label="Identity Last Four Only">
                  <input
                    className="form-control"
                    name="identity_last_four"
                    maxLength="4"
                    placeholder="Last 4 only"
                    value={visitForm.identity_last_four}
                    onChange={updateVisit}
                  />
                </Field>

                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Purpose of Visit" required>
                    <textarea
                      className="form-control"
                      name="purpose"
                      rows="3"
                      value={visitForm.purpose}
                      onChange={updateVisit}
                      required
                    />
                  </Field>
                </div>
              </div>

              <div className="modal-ftr">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() =>
                    setVisitForm(emptyVisitForm)
                  }
                >
                  Clear
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? 'Registering...'
                    : 'Register Visitor'}
                </button>
              </div>
            </form>
          )}

          {tab === 'schedule' && canWrite && (
            <form
              className="card"
              onSubmit={submitAppointment}
            >
              <div className="card-header">
                <strong>Schedule Appointment</strong>
              </div>

              <div style={{
                padding: 18,
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(230px, 1fr))',
                gap: 14
              }}>
                <Field label="Visitor Name" required>
                  <input
                    className="form-control"
                    name="visitor_name"
                    value={
                      appointmentForm.visitor_name
                    }
                    onChange={updateAppointment}
                    required
                  />
                </Field>

                <Field label="Phone Number">
                  <input
                    className="form-control"
                    name="visitor_phone"
                    value={
                      appointmentForm.visitor_phone
                    }
                    onChange={updateAppointment}
                  />
                </Field>

                <Field label="Email Address">
                  <input
                    type="email"
                    className="form-control"
                    name="visitor_email"
                    value={
                      appointmentForm.visitor_email
                    }
                    onChange={updateAppointment}
                  />
                </Field>

                <Field label="Organisation">
                  <input
                    className="form-control"
                    name="organisation"
                    value={
                      appointmentForm.organisation
                    }
                    onChange={updateAppointment}
                  />
                </Field>

                <Field label="Visit Nature" required>
                  <select
                    className="form-control"
                    name="visit_nature"
                    value={
                      appointmentForm.visit_nature
                    }
                    onChange={updateAppointment}
                    required
                  >
                    <option value="official">
                      Official Visit
                    </option>
                    <option value="personal">
                      Personal Visit
                    </option>
                  </select>
                </Field>

                <Field
                  label={
                    appointmentForm.visit_nature ===
                      'personal'
                      ? 'Host (Managing Director only)'
                      : 'Host'
                  }
                  required
                >
                  <select
                    className="form-control"
                    name="host_user_id"
                    value={
                      appointmentForm.host_user_id
                    }
                    onChange={updateAppointment}
                    required
                  >
                    <option value="">Select host</option>
                    {hostsForNature(
                      appointmentForm.visit_nature
                    ).map(host => (
                      <option
                        key={host.id}
                        value={host.id}
                      >
                        {host.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Start Time" required>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="scheduled_start"
                    value={
                      appointmentForm.scheduled_start
                    }
                    onChange={updateAppointment}
                    required
                  />
                </Field>

                <Field label="End Time">
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="scheduled_end"
                    value={
                      appointmentForm.scheduled_end
                    }
                    onChange={updateAppointment}
                  />
                </Field>

                <Field label="Location">
                  <input
                    className="form-control"
                    name="location"
                    value={appointmentForm.location}
                    onChange={updateAppointment}
                  />
                </Field>

                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Purpose" required>
                    <textarea
                      className="form-control"
                      name="purpose"
                      rows="3"
                      value={appointmentForm.purpose}
                      onChange={updateAppointment}
                      required
                    />
                  </Field>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Notes">
                    <textarea
                      className="form-control"
                      name="notes"
                      rows="2"
                      value={appointmentForm.notes}
                      onChange={updateAppointment}
                    />
                  </Field>
                </div>
              </div>

              <div className="modal-ftr">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() =>
                    setAppointmentForm(
                      emptyAppointmentForm
                    )
                  }
                >
                  Clear
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving...'
                    : 'Create Appointment'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
